import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { pluggyCredenciaisApi, alertasApi, authApi, notificacoesApi } from '../../api/endpoints'
import type { PreferenciaNotificacao } from '../../types'
import { useAuthStore } from '../../store/authStore'
import { Toggle, Input, PasswordInput, Select } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import { Card, CardHeader, CardBody, AvatarIcon } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { LoadingState } from '../../components/ui/EmptyState'
import { Modal } from '../../components/ui/Modal'
import { useToast } from '../../components/ui/Toast'
import { PageHeader } from '../../layout/PageHeader'
import { PluggyAmbiente, TipoAlerta, CanalAlerta, CANAL_ALERTA_LABEL } from '../../lib/enums'

const APP_VERSION = '1.0.0'

const ALERTAS_CONFIG: { tipo: TipoAlerta; label: string; descricao: string; icon: string; temPercentual?: boolean; temValor?: boolean }[] = [
  { tipo: TipoAlerta.OrcamentoAtingido, label: 'Orçamento atingido', descricao: 'Avisa quando o gasto chega a um percentual do limite.', icon: 'ri-pie-chart-2-line', temPercentual: true },
  { tipo: TipoAlerta.VencimentoConta, label: 'Vencimento de conta', descricao: 'Lembra lançamentos pendentes perto do vencimento.', icon: 'ri-calendar-event-line' },
  { tipo: TipoAlerta.ResumoSemanal, label: 'Resumo semanal', descricao: 'Resumo de receitas e gastos toda semana.', icon: 'ri-mail-open-line' },
  { tipo: TipoAlerta.SaldoBaixo, label: 'Saldo baixo', descricao: 'Avisa quando o saldo fica abaixo de um valor.', icon: 'ri-wallet-3-line', temValor: true },
  { tipo: TipoAlerta.FaturaCartao, label: 'Fatura do cartão', descricao: 'Lembra o fechamento e o vencimento da fatura.', icon: 'ri-bank-card-line' },
]

/** Um interruptor por tipo de notificação; a preferência é do próprio usuário. */
function PreferenciasNotificacoes() {
  const qc = useQueryClient()
  const toast = useToast()
  const chave = ['notificacoes-preferencias']
  const { data: prefs = [], isLoading, isError } = useQuery({ queryKey: chave, queryFn: notificacoesApi.preferencias })

  const alternar = useMutation({
    mutationFn: notificacoesApi.salvarPreferencia,
    onMutate: async ({ tipo, ativa }) => {
      await qc.cancelQueries({ queryKey: chave })
      const anterior = qc.getQueryData<PreferenciaNotificacao[]>(chave)
      qc.setQueryData<PreferenciaNotificacao[]>(chave, (atual = []) => atual.map((p) => (p.tipo === tipo ? { ...p, ativa } : p)))
      return { anterior }
    },
    onError: (e: Error, _vars, ctx) => {
      if (ctx?.anterior) qc.setQueryData(chave, ctx.anterior)
      toast.error(e.message || 'Não foi possível salvar a preferência.')
    },
  })

  return (
    <Card>
      <CardHeader title="Notificações" icon="ri-notification-3-line" />
      <CardBody className="space-y-3">
        <p className="text-13 text-muted">Você escolhe quais avisos receber. Os avisos de vencimento e orçamento saem de manhã.</p>
        {isLoading ? (
          <LoadingState />
        ) : isError || prefs.length === 0 ? (
          <p className="text-13 text-muted">Não foi possível carregar as preferências agora.</p>
        ) : (
          <div className="divide-y divide-border/60">
            {prefs.map((p) => (
              <div key={p.tipo} className="flex items-center justify-between gap-3 py-2.5">
                <span className="text-13 text-white">{p.rotulo}</span>
                <Toggle label="" checked={p.ativa} onChange={(v) => alternar.mutate({ tipo: p.tipo, ativa: v })} />
              </div>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  )
}

export function ConfiguracoesPage() {
  const qc = useQueryClient()
  const toast = useToast()

  const [confirmSairTodos, setConfirmSairTodos] = useState(false)
  const [saindoDeTodos, setSaindoDeTodos] = useState(false)
  const [sairErro, setSairErro] = useState<string | null>(null)

  async function sairDeTodos() {
    setSaindoDeTodos(true)
    setSairErro(null)
    try {
      await authApi.sairDeTodos()
      useAuthStore.getState().logout()
    } catch (e) {
      setSairErro((e as Error).message)
      setConfirmSairTodos(false)
      setSaindoDeTodos(false)
    }
  }

  const [clientId, setClientId] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  const [ambiente, setAmbiente] = useState<PluggyAmbiente>(PluggyAmbiente.Sandbox)
  const [pluggyErro, setPluggyErro] = useState<string | null>(null)
  const [confirmRemover, setConfirmRemover] = useState(false)

  const { data: credencial, isLoading } = useQuery({
    queryKey: ['pluggy-credenciais'],
    queryFn: pluggyCredenciaisApi.get,
    retry: false,
  })

  useEffect(() => {
    if (credencial) {
      setClientId(credencial.clientId)
      setAmbiente(credencial.ambiente)
    }
  }, [credencial])

  const salvarPluggy = useMutation({
    mutationFn: () => pluggyCredenciaisApi.salvar({ clientId, clientSecret, ambiente }),
    onSuccess: () => {
      setPluggyErro(null)
      setClientSecret('')
      qc.invalidateQueries({ queryKey: ['pluggy-credenciais'] })
      toast.success('Credenciais Pluggy salvas.')
    },
    onError: (e: Error) => setPluggyErro(e.message || 'Erro ao salvar. Verifique as credenciais.'),
  })

  const removerPluggy = useMutation({
    mutationFn: pluggyCredenciaisApi.remover,
    onSuccess: () => {
      setClientId('')
      setClientSecret('')
      setConfirmRemover(false)
      qc.invalidateQueries({ queryKey: ['pluggy-credenciais'] })
      toast.success('Credenciais Pluggy removidas.')
    },
    onError: (e: Error) => toast.error(e.message),
  })

  function handleSalvarPluggy() {
    if (!clientId.trim() || !clientSecret.trim()) {
      setPluggyErro('Informe o Client ID e o Client Secret para salvar.')
      return
    }
    salvarPluggy.mutate()
  }

  const { data: alertas = [] } = useQuery({ queryKey: ['alertas'], queryFn: alertasApi.list })

  const salvarAlerta = useMutation({
    mutationFn: (data: { tipo: TipoAlerta; canal: CanalAlerta; ativo: boolean; percentualLimite?: number; valorLimite?: number }) => {
      const existente = alertas.find((a) => a.tipo === data.tipo)
      return existente ? alertasApi.atualizar(existente.id, data) : alertasApi.salvar(data)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['alertas'] }),
    onError: (e: Error) => toast.error(e.message),
  })

  const pluggyConfigurado = !!credencial?.possuiApiKey
  const apiUrl = import.meta.env.VITE_API_URL as string

  return (
    <>
      <PageHeader title="Configurações" />

      <div className="grid gap-6 xl:grid-cols-[1fr_380px]">
        <Card>
          <CardHeader
            title="Alertas"
            icon="ri-notification-3-line"
            actions={<span className="text-13 text-muted">{alertas.filter((a) => a.ativo).length} ativo(s)</span>}
          />
          <div className="divide-y divide-border/60">
            {ALERTAS_CONFIG.map((cfg) => {
              const existente = alertas.find((a) => a.tipo === cfg.tipo)
              const ativo = existente?.ativo ?? false
              const canal = existente?.canal ?? CanalAlerta.Push
              const base = { tipo: cfg.tipo, canal, ativo, percentualLimite: existente?.percentualLimite, valorLimite: existente?.valorLimite }
              return (
                <div key={cfg.tipo} className="flex flex-wrap items-center gap-4 px-4 py-4">
                  <AvatarIcon icon={cfg.icon} tone={ativo ? 'primary' : 'muted'} />
                  <div className="min-w-[180px] flex-1">
                    <p className="font-medium text-white">{cfg.label}</p>
                    <p className="text-2xs text-muted">{cfg.descricao}</p>
                  </div>
                  {ativo && (
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="w-28">
                        <Select value={canal} aria-label={`Canal do alerta ${cfg.label}`} onChange={(e) => salvarAlerta.mutate({ ...base, canal: Number(e.target.value) as CanalAlerta })}>
                          {Object.entries(CANAL_ALERTA_LABEL).map(([valor, label]) => <option key={valor} value={valor}>{label}</option>)}
                        </Select>
                      </div>
                      {cfg.temPercentual && (
                        <div className="relative w-24">
                          <input
                            type="number" min={1} max={100}
                            defaultValue={existente?.percentualLimite ?? 80}
                            onBlur={(e) => salvarAlerta.mutate({ ...base, percentualLimite: Number(e.target.value) })}
                            className="form-control pr-7"
                            aria-label="Percentual do limite"
                          />
                          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-13 text-muted">%</span>
                        </div>
                      )}
                      {cfg.temValor && (
                        <div className="relative w-32">
                          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-13 text-muted">R$</span>
                          <input
                            type="number" min={0}
                            defaultValue={existente?.valorLimite ?? 100}
                            onBlur={(e) => salvarAlerta.mutate({ ...base, valorLimite: Number(e.target.value) })}
                            className="form-control pl-9"
                            aria-label="Valor mínimo de saldo"
                          />
                        </div>
                      )}
                    </div>
                  )}
                  <Toggle label="" checked={ativo} onChange={(v) => salvarAlerta.mutate({ ...base, ativo: v })} disabled={salvarAlerta.isPending} />
                </div>
              )
            })}
          </div>
        </Card>

        <div className="space-y-6">
          <div data-tutorial="pluggy.credenciais">
          <Card>
            <CardHeader
              title="Pluggy · Open Finance"
              icon="ri-plug-line"
              actions={<Badge variant={pluggyConfigurado ? 'income' : 'expense'}>{pluggyConfigurado ? 'Configurado' : 'Não configurado'}</Badge>}
            />
            <CardBody>
              {isLoading ? (
                <LoadingState />
              ) : (
                <div className="space-y-4">
                  <p className="text-13 text-muted">
                    Opcional. Para conectar um banco, cada pessoa usa a própria conta na Pluggy: crie a sua no Meu Pluggy (pluggy.ai),
                    copie o Client ID e o Client Secret e cole aqui. Sem isso, o Walleon funciona normalmente com contas manuais e importação de fatura (CSV).
                  </p>
                  <Input label="Client ID" value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="seu-client-id" autoComplete="off" />
                  <PasswordInput label="Client Secret" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} placeholder="seu-client-secret" autoComplete="new-password" />
                  {pluggyConfigurado && <p className="-mt-2 text-2xs text-muted">O secret salvo não é exibido. Para alterar as credenciais, informe-o novamente.</p>}
                  <Select label="Ambiente" value={ambiente} onChange={(e) => setAmbiente(Number(e.target.value) as PluggyAmbiente)}>
                    <option value={PluggyAmbiente.Sandbox}>Sandbox</option>
                    <option value={PluggyAmbiente.Producao}>Produção</option>
                  </Select>
                  {pluggyErro && <p className="flex items-center gap-1.5 rounded bg-expense/10 px-3 py-2 text-13 text-expense" role="alert"><i className="ri-error-warning-line" aria-hidden /> {pluggyErro}</p>}
                  <div className="flex gap-2">
                    <Button icon="ri-save-line" loading={salvarPluggy.isPending} onClick={handleSalvarPluggy}>Salvar credenciais</Button>
                    {pluggyConfigurado && <Button variant="soft-danger" icon="ri-delete-bin-line" onClick={() => setConfirmRemover(true)}>Remover</Button>}
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
          </div>

          <PreferenciasNotificacoes />

          <Card>
            <CardHeader title="Sessões" icon="ri-shield-keyhole-line" />
            <CardBody className="space-y-3 text-13">
              <p className="text-muted">Encerra o seu login neste navegador e em todos os outros aparelhos (celulares e navegadores). Você entra de novo com e-mail e senha.</p>
              {sairErro && <p className="flex items-center gap-1.5 rounded bg-expense/10 px-3 py-2 text-13 text-expense" role="alert"><i className="ri-error-warning-line" aria-hidden /> {sairErro}</p>}
              <Button variant="soft-danger" icon="ri-logout-box-line" loading={saindoDeTodos} onClick={() => setConfirmSairTodos(true)}>Sair de todos os aparelhos</Button>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Sobre" icon="ri-information-line" />
            <CardBody className="space-y-2 text-13">
              <div className="flex justify-between"><span className="text-muted">Versão</span><span className="text-white">{APP_VERSION}</span></div>
              <div className="flex justify-between gap-4"><span className="text-muted">API</span><span className="truncate text-white" title={apiUrl}>{apiUrl}</span></div>
              <p className="pt-2 text-muted">Proteja. Organize. Evolua.</p>
            </CardBody>
          </Card>
        </div>
      </div>

      <Modal
        open={confirmSairTodos}
        onClose={() => setConfirmSairTodos(false)}
        title="Sair de todos os aparelhos"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmSairTodos(false)}>Cancelar</Button>
            <Button variant="danger" loading={saindoDeTodos} onClick={sairDeTodos}>Sair de todos</Button>
          </>
        }
      >
        <p className="text-13 text-muted">Seu login será encerrado em todos os aparelhos, inclusive neste.</p>
      </Modal>

      <Modal
        open={confirmRemover}
        onClose={() => setConfirmRemover(false)}
        title="Remover credenciais Pluggy"
        size="sm"
        footer={
          <>
            <Button variant="light" onClick={() => setConfirmRemover(false)}>Cancelar</Button>
            <Button variant="danger" onClick={() => removerPluggy.mutate()} loading={removerPluggy.isPending}>Sim, remover</Button>
          </>
        }
      >
        <p className="text-13 text-muted">A sincronização com o Open Finance para de funcionar até você cadastrar novas credenciais.</p>
      </Modal>
    </>
  )
}
